import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { P2pSide, P2pStatus } from './types';

// P2P History filter type - simplified for single tab
export type P2pHistoryFilter = {
  symbol?: string; // Market symbol (e.g., "BTCUSDT")
  side?: P2pSide;
  status?: P2pStatus[]; // Array to support multiple status selection
  transactTimeFloor?: string;
  transactTimeCeil?: string;
};

// P2P History has only one tab type
export type p2pHistoryTabType = 'openOrders';

// Define the state type - simplified for single tab
type P2pHistoryStateType = {
  p2pHistory: P2pHistoryFilter;
};

const initialState: P2pHistoryStateType = {
  p2pHistory: {
    symbol: '',
    status: [],
    side: undefined,
    transactTimeFloor: '',
    transactTimeCeil: ''
  }
};

const p2pHistorySlice = createSlice({
  name: 'p2pHistory',
  initialState,
  reducers: {
    // Set asset filter
    setP2pAssetFilter: (state, action: PayloadAction<{ asset: string }>) => {
      const { asset } = action.payload;
      // Also set symbol for backward compatibility
      state.p2pHistory.symbol = asset;
    },

    // Set symbol filter (legacy support)
    setP2pSymbolFilter: (state, action: PayloadAction<{ symbol: string }>) => {
      const { symbol } = action.payload;
      state.p2pHistory.symbol = symbol;
    },

    // Set side filter
    setP2pSideFilter: (state, action: PayloadAction<{ side?: P2pSide }>) => {
      const { side } = action.payload;
      state.p2pHistory.side = side;
    },

    // Set status filter - now supports multiple statuses
    setP2pStatusFilter: (
      state,
      action: PayloadAction<{ status: P2pStatus[] }>
    ) => {
      const { status } = action.payload;
      state.p2pHistory.status = status;
    },

    // Clear status filter
    clearP2pStatusFilter: (state) => {
      state.p2pHistory.status = [];
    },

    // Add single status to filter
    addP2pStatusFilter: (
      state,
      action: PayloadAction<{ status: P2pStatus }>
    ) => {
      const { status } = action.payload;
      if (!state.p2pHistory.status) {
        state.p2pHistory.status = [];
      }
      if (!state.p2pHistory.status.includes(status)) {
        state.p2pHistory.status.push(status);
      }
    },

    // Remove single status from filter
    removeP2pStatusFilter: (
      state,
      action: PayloadAction<{ status: P2pStatus }>
    ) => {
      const { status } = action.payload;
      if (state.p2pHistory.status) {
        state.p2pHistory.status = state.p2pHistory.status.filter(
          (s) => s !== status
        );
      }
    },

    // Set date filters using P2P specific field names
    setP2pDateFilters: (
      state,
      action: PayloadAction<{
        transactTimeFloor?: string;
        transactTimeCeil?: string;
      }>
    ) => {
      const { transactTimeFloor, transactTimeCeil } = action.payload;
      if (transactTimeFloor !== undefined)
        state.p2pHistory.transactTimeFloor = transactTimeFloor;
      if (transactTimeCeil !== undefined)
        state.p2pHistory.transactTimeCeil = transactTimeCeil;
    },

    // Set all filters at once
    setP2pAllFilters: (
      state,
      action: PayloadAction<Partial<P2pHistoryFilter>>
    ) => {
      const filters = action.payload;
      state.p2pHistory = {
        ...state.p2pHistory,
        ...filters
      };
    },

    // Clear all filters
    clearP2pHistoryFilters: (state) => {
      state.p2pHistory = {
        ...initialState.p2pHistory
      };
    }
  }
});

export const {
  setP2pSymbolFilter,
  setP2pAssetFilter,
  setP2pSideFilter,
  setP2pStatusFilter,
  clearP2pStatusFilter,
  addP2pStatusFilter,
  removeP2pStatusFilter,
  setP2pDateFilters,
  setP2pAllFilters,
  clearP2pHistoryFilters
} = p2pHistorySlice.actions;

export default p2pHistorySlice.reducer;
